import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Row, Col, Table, Tabs, Button, Divider, Typography, notification, Tooltip } from "antd";
import EnterpriseInfo from "./EnterpriseInfo";
import AssetInfo from "./AssetInfo";
import OwnerInfo from './OwnerInfo';
import BuildInfoForm from "../Building/BuildInfoForm";
import NonOrdinaryBuildingForm from "../Building/NonOrdinaryBuildingForm";
import axios from "axios";
import "./Enterprise.css";
import { useTranslation } from 'react-i18next';

import { AppleOutlined, AndroidOutlined } from '@ant-design/icons';

const { Title } = Typography;

const Enterprises = props => {
  const { t } = useTranslation();

  // Keyed by ENTERPRISE_TYPE.apiPath — picks the per-kind detail form for
  // the inner "Details" tab. Enterprise types without an entry here
  // (Farm, Telecom, Electrical, Transport) just don't show the tab.
  const components = {
    'building':                BuildInfoForm,
    'non-ordinary-building':   NonOrdinaryBuildingForm,
  }

  const [managementTypes, setManagementTypes] = useState([]);
  const [dataState, setDataState] = useState({ data: [] });
  const [listState, setListState] = useState({
    pagination: { pageSize: 10, current: 1 },
    filters: {},
    sorter: {}
  });
  const [loaderState, setLoaderState] = useState(false);
  const [totalPageState, setTotalPageState] = useState(0);
  const [editState, setEditState] = useState({ enterprise: {}, valid: false });
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1"); // "1" is the key for the Info tab

  const apiPath = props.type.apiPath;

  const handleTableChange = (pagination, filters, sorter) => {
    setListState({ pagination: pagination, filters: filters, sorter: sorter });
  };

  const saveEnterprise = () => {
    if (!isEditMode || !editState.valid || editState.detailsValid === false) {
      return;
    }

    console.log("[ENTERPRISES] = " + JSON.stringify(editState));
    props.startLoading();

    // Determine whether to create a new enterprise or update an existing one
    const isNewEnterprise = editState.enterprise.id === -1;
    const endpoint = process.env.REACT_APP_API_ENDPOINT + "/" + apiPath + "/save";
    const enterpriseData = { ...editState.enterprise };

    // If creating a new enterprise, ensure id is null as backend expects
    if (isNewEnterprise) {
      enterpriseData.id = null;
    }

    axios
      .post(endpoint, enterpriseData)
      .then(({ data }) => {
        setSelectedRowKey(data.id);
        // Keep the saved id in editState (reloadEnterprises can't on create: stale -1 in its closure).
        setEditState(prev => ({ ...prev, enterprise: data, valid: true }));
        setIsEditMode(false);
        reloadEnterprises();
        setTimeout(() => { props.stopLoading("success"); }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => { props.stopLoading("failure"); }, 1000);
      });
  };

  const newEnterprise = () => {
    const newEnterprise = { id: -1 };
    setDataState({
      data: [
        newEnterprise,
        ...(dataState.data)
      ]
    });
    setEditState({ enterprise: newEnterprise, valid: false });
    setIsEditMode(true);
    setSelectedRowKey(-1);
    setActiveTabKey("1"); // Set active tab to "Info" tab
  };

  const cancelEdit = () => {
    // If we're canceling a new enterprise, remove it from the temporary display
    if (editState.enterprise.id === -1) {
      setDataState({
        data: dataState.data.filter(enterprise => enterprise.id !== -1)
      });
      setSelectedRowKey(null);
      setEditState({ enterprise: {}, valid: false });
    } else {
      // If we're canceling an edit to an existing enterprise, remember its ID
      const enterpriseId = editState.enterprise.id;
      // We'll reselect this enterprise after reloading
      setSelectedRowKey(enterpriseId);
    }

    setIsEditMode(false);
    reloadEnterprises();
  };

  const reloadEnterprises = () => {
    // Remember the currently selected enterprise ID before reloading
    const currentSelectedId = selectedRowKey;

    setLoaderState(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/" + apiPath + "/", {
        params: {
          pageSize: listState.pagination.pageSize,
          pageNumber: listState.pagination.current,
          sortField: listState.sorter.field,
          sortOrder: listState.sorter.order,
          ...listState.filters
        }
      })
      .then(({ data }) => {
        setDataState({ data: data.data });
        setTotalPageState(data.totalCount);

        // Clear selection if there's no data
        if (data.data.length === 0) {
          setEditState({ enterprise: {}, valid: false });
          setSelectedRowKey(null);
        }
        // If there was a selected enterprise, find it in the new data and update editState
        else if (currentSelectedId !== null && currentSelectedId !== -1) {
          const selectedEnterprise = data.data.find(enterprise => enterprise.id === currentSelectedId);
          if (selectedEnterprise) {
            setEditState({ enterprise: selectedEnterprise, valid: true });
            setSelectedRowKey(currentSelectedId);
          }
        }
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(function () {
        setLoaderState(false);
      });
  };

  const importEnterprises = () => {
    notification.open({
      placement: "bottomRight",
      message: 'RETRIEVE DATA FROM EXTERNAL DATABASES ',
      description:
        'Function currently not available.',
    });
  };

  const editEnterprise = id => {
    const toEdit = dataState.data.find(i => i.id === id);
    // Create a deep copy to avoid reference issues
    const toEditCopy = JSON.parse(JSON.stringify(toEdit));
    setEditState({ enterprise: toEditCopy, valid: true });
    setIsEditMode(true);
    setSelectedRowKey(id);
  };

  const showEnterprise = id => {
    const toShow = dataState.data.find(i => i.id === id);
    setEditState({ enterprise: toShow, valid: true });
    setIsEditMode(false);
    setSelectedRowKey(id);
  };

  const deleteEnterprise = id => {
    const toDelete = dataState.data.find(i => i.id === id);
    props.startLoading();
    axios
      .delete(process.env.REACT_APP_API_ENDPOINT + "/" + apiPath + "/delete", {
        params: {
          id: toDelete.id
        }
      })
      .then(({ data }) => {
        reloadEnterprises();
        setEditState({ enterprise: {}, valid: false });
        setSelectedRowKey(null);
        setTimeout(() => { props.stopLoading("success"); }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => { props.stopLoading("failure"); }, 1000);
      });
  };

  const enterpriseChange = (formData, valid) => {
    var newEnterprise = { ...editState };
    newEnterprise.enterprise.vat = formData.vat.value;
    newEnterprise.enterprise.geometry = formData.geometry.value;
    newEnterprise.enterprise.name = formData.name.value;
    newEnterprise.enterprise.country = formData.country.value;
    newEnterprise.enterprise.county = formData.county.value;
    newEnterprise.enterprise.town = formData.town.value;
    newEnterprise.enterprise.postcode = formData.postcode.value;
    newEnterprise.enterprise.road = formData.road.value;
    newEnterprise.enterprise.houseNumber = formData.houseNumber.value;
    newEnterprise.enterprise.description = formData.description.value;
    newEnterprise.enterprise.managementType = formData.managementType.value;
    newEnterprise.enterprise.coordinatesInferred = formData.coordinatesInferred?.value;
    newEnterprise.valid = valid;
    setEditState(newEnterprise);
  };

  const ownerChange = (ownersIds) => {
    var newEnterprise = { ...editState };
    newEnterprise.enterprise.ownersIds = ownersIds;
    setEditState(newEnterprise);
  };

  // Keep the Details tab's validity separate so it can't override the Info tab's.
  const detailsInfoChange = (newRecord) => {
    setEditState(prev => ({
      ...prev,
      enterprise: newRecord.enterprise,
      detailsValid: newRecord.valid
    }));
  };

  const handleTabChange = (key) => {
    setActiveTabKey(key);
  };

  const getDetailsInfo = () => {
    const DetailsComponent = components[apiPath];
    if (DetailsComponent) {
      return (
        <TabPane
          tab={
            <span>
              {" "}
              <AppleOutlined /> Details
            </span>
          }
          key="2"
        >
          <DetailsComponent
            record={editState}
            parentCallback={detailsInfoChange}
            isEditMode={isEditMode}
          />
        </TabPane>
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    reloadEnterprises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listState, props.type]);

  useEffect(() => {
    // Updated to use the new enterprise API endpoint for management types
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/enterprises/management-types")
      .then(({ data }) => {
        setManagementTypes(data);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    console.log("Enterprise type changed to:", props.type.name);
    // Clear selection when type changes
    setEditState({ enterprise: {}, valid: false });
    setSelectedRowKey(null);
    setIsEditMode(false);
    setDataState({ data: [] });
  }, [props.type.name]);

  const { TabPane } = Tabs;

  // Required fields, surfaced in the Save tooltip when still empty.
  const requiredInfoFields = [
    { key: 'name', label: 'Name' },
    { key: 'country', label: 'Country' },
    { key: 'town', label: 'Town' }
  ];
  const requiredDetailFieldsByType = {
    'non-ordinary-building': [{ key: 'subtype', label: 'Subtype (Details tab)' }]
  };
  const requiredFields = [...requiredInfoFields, ...(requiredDetailFieldsByType[apiPath] || [])];
  const missingRequiredFields = isEditMode
    ? requiredFields
        .filter(({ key }) => {
          const value = editState.enterprise?.[key];
          return !value || (typeof value === 'string' && value.trim() === '');
        })
        .map(({ label }) => label)
    : [];
  const saveDisabledReason = missingRequiredFields.length > 0
    ? `Required fields still empty: ${missingRequiredFields.join(', ')}`
    : '';

  // Assets need a persisted parent; block the Assets tab until the enterprise is saved.
  const enterpriseNotSaved = !editState.enterprise?.id || editState.enterprise.id === -1;

  const columns = [
    {
      title: "Vat",
      dataIndex: "vat"
    },
    {
      title: "Name",
      dataIndex: "name"
    },
    {
      title: "Management",
      dataIndex: "managementType",
      render: (text) => text ? t(`enterprises.managementType.${text}`) : ""
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <span>
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={(e) => {
              e.stopPropagation();
              editEnterprise(record.id);
            }}
          >
            Edit
          </Button>
          <Divider type="vertical" />
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={() => deleteEnterprise(record.id)}
          >
            Delete
          </Button>
        </span>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>{props.type.name}</Title>
        </Col>
        <Col>
          <Button key="1"
            disabled={isEditMode}
            onClick={() => reloadEnterprises()}>
            Refresh
          </Button>
          <Button key="2"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => newEnterprise()}>
            New
          </Button>
          <Tooltip title={saveDisabledReason}>
            <span style={{ marginLeft: 8 }}>
              <Button
                key="3"
                type="primary"
                disabled={!isEditMode || !editState.valid || editState.detailsValid === false || missingRequiredFields.length > 0}
                onClick={() => saveEnterprise()}>
                Save
              </Button>
            </span>
          </Tooltip>
          <Button
            key="5"
            style={{ marginLeft: 8 }}
            onClick={() => cancelEdit()}
            disabled={!isEditMode}>
            Cancel
          </Button>
          <Button
            key="4"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => importEnterprises()}>
            Import
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24} style={{ height: 250 }}>
          <Table
            size="small"
            scroll={{ y: 150 }}
            columns={columns}
            rowKey={record => record.id}
            dataSource={dataState.data}
            pagination={{ ...listState.pagination, total: totalPageState }}
            loading={loaderState}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => {
                if (!isEditMode) {
                  showEnterprise(record.id);
                }
              },
              className: record.id === selectedRowKey ? 'selected-row' : '',
              style: isEditMode ? { cursor: 'not-allowed' } : {}
            })}
            rowClassName={(enterprise) => enterprise.id === selectedRowKey ? 'selected-row' : ''}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Tabs activeKey={activeTabKey} onChange={handleTabChange}>
            <TabPane
              tab={
                <span>
                  {" "}
                  <AppleOutlined /> Info
                </span>
              }
              key="1"
            >
              <EnterpriseInfo
                type={props.type}
                record={editState}
                parentCallback={enterpriseChange}
                managementTypes={managementTypes}
                isEditMode={isEditMode}
              ></EnterpriseInfo>
            </TabPane>
            {getDetailsInfo()}
            <TabPane
              tab={
                <span>
                  <AndroidOutlined />
                  Owner profile
                </span>
              }
              key="3"
            >
              <OwnerInfo
                record={editState}
                parentCallback={ownerChange}
                isEditMode={isEditMode}
              ></OwnerInfo>
            </TabPane>
            <TabPane
              tab={
                <Tooltip title={enterpriseNotSaved ? "Save the enterprise first, then add its assets" : null}>
                  <span>
                    <AndroidOutlined />
                    Assets & production
                  </span>
                </Tooltip>
              }
              key="4"
              disabled={enterpriseNotSaved}
            >
              <AssetInfo
                type={props.type}
                record={editState}
                isEditMode={isEditMode}
              ></AssetInfo>
            </TabPane>
          </Tabs>
        </Col>
      </Row>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    startLoading: () => dispatch({ type: 'START_LOADING' }),
    stopLoading: (val) => dispatch({ type: 'STOP_LOADING', status: val })
  };
};

export default connect(null, mapDispatchToProps)(Enterprises);